import { useMemo } from 'react';
import { EngagementByActivitiesChart } from './EngagementByActivitiesChart';
import type { ActivityEngagementSeries } from '../../types';

function formatShortDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' });
}

function formatDateLabel(dateStr: string, compact?: boolean): string {
  if (compact) {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return formatShortDate(dateStr);
}

// Family-grouped palettes so providers from the same family (claude / openai /
// gemini / other) get visually related colors. Assignment is order-stable per
// family so the same provider keeps the same color across re-renders.
const PROVIDER_PALETTES: Record<'claude' | 'openai' | 'gemini' | 'other', string[]> = {
  claude: ['#8b5cf6', '#a78bfa', '#6d28d9', '#c4b5fd', '#5b21b6'],
  openai: ['#10b981', '#34d399', '#047857', '#6ee7b7', '#065f46'],
  gemini: ['#f59e0b', '#fbbf24', '#b45309', '#fcd34d', '#92400e'],
  other:  ['#3b82f6', '#ef4444', '#ec4899', '#14b8a6', '#f97316'],
};

function detectProviderFamily(name: string): 'claude' | 'openai' | 'gemini' | 'other' {
  const n = name.toLowerCase();
  if (n.includes('claude') || n.includes('anthropic')) return 'claude';
  if (n.includes('gpt') || n.includes('openai') || /\bo[134]\b/.test(n)) return 'openai';
  if (n.includes('gemini') || n.includes('google')) return 'gemini';
  return 'other';
}

function buildProviderColorMap(providers: string[]): Record<string, string> {
  const byFamily: Record<'claude' | 'openai' | 'gemini' | 'other', string[]> = {
    claude: [], openai: [], gemini: [], other: [],
  };
  for (const p of providers) byFamily[detectProviderFamily(p)].push(p);
  const map: Record<string, string> = {};
  (Object.keys(byFamily) as Array<keyof typeof byFamily>).forEach((family) => {
    const palette = PROVIDER_PALETTES[family];
    byFamily[family].slice().sort().forEach((name, i) => {
      map[name] = palette[i % palette.length];
    });
  });
  return map;
}

interface WeeklyBucket {
  label: string;
  submissions: number;
  activeUsers: number;
  newUsers: number;
  llmCalls: number;
  estimatedCostUsd: number;
}

function aggregateWeekly<T extends { date: string }>(
  dailyData: T[],
  aggregator: (bucket: T[]) => Omit<WeeklyBucket, 'label'>,
): WeeklyBucket[] {
  if (dailyData.length === 0) return [];
  const weeks: WeeklyBucket[] = [];
  for (let i = 0; i < dailyData.length; i += 7) {
    const chunk = dailyData.slice(i, i + 7);
    const startDate = chunk[0].date;
    const endDate = chunk[chunk.length - 1].date;
    const label = `${formatDateLabel(startDate, true)} - ${formatDateLabel(endDate, true)}`;
    weeks.push({ label, ...aggregator(chunk) });
  }
  return weeks;
}

interface AdminStats {
  totalUsers: number;
  totalSubmissions: number;
  totalQuestions: number;
  dailyTrend: { date: string; submissions: number; llmCalls: number; estimatedCostUsd: number }[];
  dailyProviderBreakdown: { date: string; provider: string; llmCalls: number; estimatedCostUsd: number }[];
  dailyUsers: { date: string; activeUsers: number; newUsers: number }[];
  activityEngagement?: ActivityEngagementSeries[];
}

interface UsageTabProps {
  stats: AdminStats;
  rangeLabel: string;
  isWeekly: boolean;
}

export function UsageTab({ stats, rangeLabel, isWeekly }: UsageTabProps) {
  const dailyTrend = stats.dailyTrend ?? [];
  const dailyUsers = stats.dailyUsers ?? [];
  const dailyProviderBreakdown = (stats.dailyProviderBreakdown ?? []).filter(r => r.provider !== 'none');

  return (
    <>
      <div className="stat-cards">
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{stats.totalUsers}</div>
          <div className="stat-card-label">Total Users</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{stats.totalSubmissions}</div>
          <div className="stat-card-label">Total Submissions</div>
        </div>
        <div className="stat-card stat-card--fact">
          <div className="stat-card-value">{stats.totalQuestions}</div>
          <div className="stat-card-label">Total Questions</div>
        </div>
      </div>

      {isWeekly ? (
        <WeeklyUsersChart dailyUsers={dailyUsers} rangeLabel={rangeLabel} />
      ) : (
        <DailyUsersChart dailyUsers={dailyUsers} rangeLabel={rangeLabel} />
      )}

      {isWeekly ? (
        <WeeklySubmissionsChart dailyTrend={dailyTrend} rangeLabel={rangeLabel} />
      ) : (
        <DailySubmissionsChart dailyTrend={dailyTrend} rangeLabel={rangeLabel} />
      )}

      <ProviderChart
        dailyTrend={dailyTrend}
        dailyProviderBreakdown={dailyProviderBreakdown}
        rangeLabel={rangeLabel}
        isWeekly={isWeekly}
      />

      <EngagementByActivitiesChart series={stats.activityEngagement ?? []} weekly={isWeekly} />
    </>
  );
}

// ── Chart Components ────────────────────────────────────────────────────

/**
 * Show every Nth date label once the bars outnumber the room for them.
 *
 * Every bar keeps its own value on top; it is the DATES underneath that stop
 * fitting. At 8 weeks that is 56 of them, so one label a week is the readable
 * choice — the bars stay daily, which is the point.
 */
function labelEvery(count: number): number {
  if (count > 42) return 7;
  if (count > 28) return 3;
  return 1;
}

function DailyUsersChart({ dailyUsers, rangeLabel }: { dailyUsers: { date: string; activeUsers: number; newUsers: number }[]; rangeLabel: string }) {
  const maxUserBar = Math.max(...dailyUsers.map(d => d.activeUsers + d.newUsers), 1);
  const compact = dailyUsers.length > 14;
  const every = labelEvery(dailyUsers.length);

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <div className="chart-header">
        <h3>Daily Users — solved ({rangeLabel})</h3>
        <div className="trend-legend">
          <span className="legend-item"><span className="legend-dot" style={{ background: '#f97316' }} />New</span>
          <span className="legend-item" title="Distinct users who submitted an answer that day. Narrower than Retention's DAU, which also counts opening the app.">
            <span className="legend-dot" style={{ background: '#0ea5e9' }} />Solved
          </span>
        </div>
      </div>
      <div className={`trend-chart ${compact ? 'trend-chart-compact' : ''}`}>
        {dailyUsers.map((day, i) => (
          <div key={day.date} className="trend-bar-group">
            <div className="trend-bars">
              <StackedUserBar active={day.activeUsers} newUsers={day.newUsers} maxTotal={maxUserBar} compact={compact} />
            </div>
            <span className="trend-date">
              {/* Counted from the END, so the most recent day is always labelled. */}
              {(dailyUsers.length - 1 - i) % every === 0
                ? (compact ? formatDateLabel(day.date, true) : formatShortDate(day.date))
                : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StackedUserBar({ active, newUsers, maxTotal, compact }: { active: number; newUsers: number; maxTotal: number; compact: boolean }) {
  const total = active + newUsers;
  const totalHeightPct = (total / maxTotal) * 100;
  const width = compact ? 26 : 18;
  return (
    <div className="trend-bar-wrapper" title={`Solved: ${active} • New: ${newUsers}`}>
      <span className="trend-bar-value">{total}</span>
      <div style={{ width, height: `${totalHeightPct}%`, minHeight: total > 0 ? 4 : 0, display: 'flex', flexDirection: 'column' }}>
        {newUsers > 0 && (
          <div style={{
            width: '100%',
            flexBasis: `${(newUsers / total) * 100}%`,
            background: '#f97316',
            borderRadius: '3px 3px 0 0',
          }} />
        )}
        {active > 0 && (
          <div style={{
            width: '100%',
            flexBasis: `${(active / total) * 100}%`,
            background: '#0ea5e9',
            borderRadius: newUsers > 0 ? 0 : '3px 3px 0 0',
          }} />
        )}
      </div>
    </div>
  );
}

function WeeklyUsersChart({ dailyUsers, rangeLabel }: { dailyUsers: { date: string; activeUsers: number; newUsers: number }[]; rangeLabel: string }) {
  const weeks = useMemo(() => aggregateWeekly(dailyUsers, (chunk) => ({
    submissions: 0, activeUsers: chunk.reduce((s, d) => s + d.activeUsers, 0),
    newUsers: chunk.reduce((s, d) => s + d.newUsers, 0), llmCalls: 0, estimatedCostUsd: 0,
  })), [dailyUsers]);
  const maxBar = Math.max(...weeks.map(w => w.activeUsers + w.newUsers), 1);

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <div className="chart-header">
        <h3>Weekly Users — solved ({rangeLabel})</h3>
        <div className="trend-legend">
          <span className="legend-item"><span className="legend-dot" style={{ background: '#f97316' }} />New</span>
          <span className="legend-item" title="Distinct users who submitted an answer that day. Narrower than Retention's DAU, which also counts opening the app.">
            <span className="legend-dot" style={{ background: '#0ea5e9' }} />Solved
          </span>
        </div>
      </div>
      <div className="trend-chart">
        {weeks.map((w) => (
          <div key={w.label} className="trend-bar-group">
            <div className="trend-bars">
              <StackedUserBar active={w.activeUsers} newUsers={w.newUsers} maxTotal={maxBar} compact={false} />
            </div>
            <span className="trend-date trend-date-week">{w.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DailySubmissionsChart({ dailyTrend, rangeLabel }: { dailyTrend: { date: string; submissions: number }[]; rangeLabel: string }) {
  const maxSubmissions = Math.max(...dailyTrend.map(d => d.submissions), 1);
  const compact = dailyTrend.length > 14;
  const every = labelEvery(dailyTrend.length);

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <div className="chart-header"><h3>Daily Submissions ({rangeLabel})</h3></div>
      <div className={`trend-chart ${compact ? 'trend-chart-compact' : ''}`}>
        {dailyTrend.map((day, i) => (
          <div key={day.date} className="trend-bar-group">
            <div className="trend-bars">
              <div className="trend-bar-wrapper" title={`Submissions: ${day.submissions}`}>
                <span className="trend-bar-value">{day.submissions}</span>
                <div className="trend-bar" style={{ height: `${Math.max(4, (day.submissions / maxSubmissions) * 100)}%`, background: '#3b82f6' }} />
              </div>
            </div>
            <span className="trend-date">
              {(dailyTrend.length - 1 - i) % every === 0
                ? (compact ? formatDateLabel(day.date, true) : formatShortDate(day.date))
                : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function WeeklySubmissionsChart({ dailyTrend, rangeLabel }: { dailyTrend: { date: string; submissions: number }[]; rangeLabel: string }) {
  const weeks = useMemo(() => aggregateWeekly(dailyTrend, (chunk) => ({
    submissions: chunk.reduce((s, d) => s + d.submissions, 0),
    activeUsers: 0, newUsers: 0, llmCalls: 0, estimatedCostUsd: 0,
  })), [dailyTrend]);
  const maxBar = Math.max(...weeks.map(w => w.submissions), 1);

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <div className="chart-header"><h3>Weekly Submissions ({rangeLabel})</h3></div>
      <div className="trend-chart">
        {weeks.map((w) => (
          <div key={w.label} className="trend-bar-group">
            <div className="trend-bars">
              <div className="trend-bar-wrapper" title={`Submissions: ${w.submissions}`}>
                <span className="trend-bar-value">{w.submissions}</span>
                <div className="trend-bar" style={{ height: `${Math.max(4, (w.submissions / maxBar) * 100)}%`, background: '#3b82f6' }} />
              </div>
            </div>
            <span className="trend-date trend-date-week">{w.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProviderChart({ dailyTrend, dailyProviderBreakdown, rangeLabel, isWeekly }: {
  dailyTrend: { date: string }[];
  dailyProviderBreakdown: { date: string; provider: string; llmCalls: number; estimatedCostUsd: number }[];
  rangeLabel: string;
  isWeekly: boolean;
}) {
  const providerDates = new Map<string, Map<string, { calls: number; cost: number }>>();
  const allProviders = new Set<string>();
  for (const row of dailyProviderBreakdown) {
    allProviders.add(row.provider);
    if (!providerDates.has(row.date)) providerDates.set(row.date, new Map());
    providerDates.get(row.date)!.set(row.provider, { calls: row.llmCalls, cost: row.estimatedCostUsd });
  }
  const providerList = Array.from(allProviders).sort();
  const colorMap = buildProviderColorMap(providerList);

  const periods: { key: string; label: string; isWeek: boolean; providers: Map<string, number> }[] = [];
  if (isWeekly) {
    for (let i = 0; i < dailyTrend.length; i += 7) {
      const chunk = dailyTrend.slice(i, i + 7);
      const startDate = chunk[0].date;
      const endDate = chunk[chunk.length - 1].date;
      const label = `${formatDateLabel(startDate, true)} - ${formatDateLabel(endDate, true)}`;
      const providers = new Map<string, number>();
      for (const day of chunk) {
        const dp = providerDates.get(day.date);
        if (dp) { for (const [p, v] of dp) providers.set(p, (providers.get(p) || 0) + v.calls); }
      }
      periods.push({ key: startDate, label, isWeek: true, providers });
    }
  } else {
    for (const day of dailyTrend) {
      const providers = new Map<string, number>();
      const dp = providerDates.get(day.date);
      if (dp) { for (const [p, v] of dp) providers.set(p, v.calls); }
      const compact = dailyTrend.length > 14;
      periods.push({
        key: day.date,
        label: compact ? formatDateLabel(day.date, true) : formatShortDate(day.date),
        isWeek: false,
        providers,
      });
    }
  }

  const periodTotals = periods.map(p => Array.from(p.providers.values()).reduce((s, v) => s + v, 0));
  const maxTotal = Math.max(...periodTotals, 1);
  const compact = !isWeekly && dailyTrend.length > 14;

  return (
    <div className="chart-card" style={{ marginBottom: 24 }}>
      <div className="chart-header">
        <h3>LLM Calls by Provider ({rangeLabel})</h3>
        <div className="trend-legend">
          {providerList.map(p => (
            <span key={p} className="legend-item">
              <span className="legend-dot" style={{ background: colorMap[p] }} />
              <span style={{ textTransform: 'capitalize' }}>{p}</span>
            </span>
          ))}
        </div>
      </div>
      {providerList.length === 0 ? <p className="no-data">No data</p> : (
        <div className={`trend-chart ${compact ? 'trend-chart-compact' : ''}`}>
          {periods.map((period, idx) => {
            const total = periodTotals[idx];
            const stackHeightPct = total === 0 ? 0 : Math.max(4, (total / maxTotal) * 100);
            const tooltip = providerList
              .map(p => `${p}: ${period.providers.get(p) || 0}`)
              .join('\n');
            return (
              <div key={period.key} className="trend-bar-group">
                <div className="trend-bars">
                  <div className="trend-bar-wrapper" title={tooltip}>
                    <span className="trend-bar-value">{total}</span>
                    <div
                      className="trend-bar trend-bar-stacked"
                      style={{ height: `${stackHeightPct}%` }}
                    >
                      {total > 0 && providerList.map(p => {
                        const calls = period.providers.get(p) || 0;
                        if (calls === 0) return null;
                        return (
                          <div
                            key={p}
                            className="trend-bar-segment"
                            style={{ flexGrow: calls, background: colorMap[p] }}
                            title={`${p}: ${calls} calls`}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
                <span className={`trend-date${period.isWeek ? ' trend-date-week' : ''}`}>{period.label}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

